import { useEffect, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from './useCurrentAppUser';
import { subDays } from 'date-fns';

export interface EscalationNotification {
  id: string;
  session_id: string;
  task_id: string | null;
  question: string;
  response: string | null;
  status: string;
  priority: string | null;
  escalated_at: string;
  responded_at: string | null;
  type: 'pending_for_me' | 'responded_to_me';
  from_user_name: string | null;
  to_user_name: string | null;
}

const PENDING_QUERY_KEY = 'escalation-notifications-pending';
const RESPONDED_QUERY_KEY = 'escalation-notifications-responded';
const STORAGE_KEY = 'read-escalation-notification-ids';

export function useEscalationNotifications() {
  const { data: appUser } = useCurrentAppUser();
  const queryClient = useQueryClient();
  const appUserId = appUser?.id;

  const [readIds, setReadIds] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  // Query 1: Pending escalations assigned to me
  const { data: pendingForMe = [] } = useQuery({
    queryKey: [PENDING_QUERY_KEY, appUserId],
    queryFn: async (): Promise<EscalationNotification[]> => {
      if (!appUserId) return [];

      const { data, error } = await supabase
        .from('session_escalations')
        .select(`
          id, session_id, task_id, question, response, status, priority, escalated_at, responded_at,
          escalated_by_user:app_users!session_escalations_escalated_by_fkey(display_name, first_name, last_name)
        `)
        .eq('escalated_to', appUserId)
        .eq('status', 'pending')
        .order('escalated_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('[useEscalationNotifications] pending error:', error);
        return [];
      }

      return (data ?? []).map((row: any) => ({
        id: row.id,
        session_id: row.session_id,
        task_id: row.task_id ?? null,
        question: row.question,
        response: row.response,
        status: row.status,
        priority: row.priority,
        escalated_at: row.escalated_at,
        responded_at: row.responded_at,
        type: 'pending_for_me' as const,
        from_user_name: row.escalated_by_user?.display_name
          || [row.escalated_by_user?.first_name, row.escalated_by_user?.last_name].filter(Boolean).join(' ')
          || null,
        to_user_name: null,
      }));
    },
    enabled: !!appUserId,
    staleTime: 2 * 60 * 1000,
  });

  // Query 2: Responded escalations that I created (last 7 days)
  const { data: respondedToMe = [] } = useQuery({
    queryKey: [RESPONDED_QUERY_KEY, appUserId],
    queryFn: async (): Promise<EscalationNotification[]> => {
      if (!appUserId) return [];

      const sevenDaysAgo = subDays(new Date(), 7).toISOString();

      const { data, error } = await supabase
        .from('session_escalations')
        .select(`
          id, session_id, task_id, question, response, status, priority, escalated_at, responded_at,
          escalated_to_user:app_users!session_escalations_escalated_to_fkey(display_name, first_name, last_name)
        `)
        .eq('escalated_by', appUserId)
        .eq('status', 'responded')
        .gte('responded_at', sevenDaysAgo)
        .order('responded_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('[useEscalationNotifications] responded error:', error);
        return [];
      }

      return (data ?? []).map((row: any) => ({
        id: row.id,
        session_id: row.session_id,
        task_id: row.task_id ?? null,
        question: row.question,
        response: row.response,
        status: row.status,
        priority: row.priority,
        escalated_at: row.escalated_at,
        responded_at: row.responded_at,
        type: 'responded_to_me' as const,
        from_user_name: null,
        to_user_name: row.escalated_to_user?.display_name
          || [row.escalated_to_user?.first_name, row.escalated_to_user?.last_name].filter(Boolean).join(' ')
          || null,
      }));
    },
    enabled: !!appUserId,
    staleTime: 2 * 60 * 1000,
  });

  const allNotifications = [...pendingForMe, ...respondedToMe];
  const unreadCount = allNotifications.filter(n => !readIds.has(n.id)).length;

  const markAsRead = useCallback((escalationId: string) => {
    setReadIds(prev => {
      const next = new Set(prev);
      next.add(escalationId);
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  }, []);

  // Realtime subscription
  useEffect(() => {
    if (!appUserId) return;

    const channel = supabase
      .channel('escalation-notifications-topbar')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'session_escalations' },
        (payload) => {
          const row = payload.new as any;
          if (row?.escalated_to === appUserId) {
            queryClient.invalidateQueries({ queryKey: [PENDING_QUERY_KEY, appUserId] });
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'session_escalations' },
        (payload) => {
          const row = payload.new as any;
          // If responded, notify the creator
          if (row?.status === 'responded') {
            queryClient.invalidateQueries({ queryKey: [RESPONDED_QUERY_KEY, appUserId] });
          }
          // If pending escalation was resolved, refresh pending list
          if (row?.escalated_to === appUserId) {
            queryClient.invalidateQueries({ queryKey: [PENDING_QUERY_KEY, appUserId] });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [appUserId, queryClient]);

  return {
    pendingForMe,
    respondedToMe,
    allNotifications,
    unreadCount,
    markAsRead,
  };
}

// Exported query keys for external invalidation
export const ESCALATION_NOTIFICATION_QUERY_KEYS = {
  pending: PENDING_QUERY_KEY,
  responded: RESPONDED_QUERY_KEY,
};
